import { useEffect, useRef, useState } from "react";
import { Divider, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { AvatarInfo, AppDrawer, SearchBar, InfiniteScroll } from "components";
import { KeyConstant, LangConstant } from "const";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/system";
import { useAccountList, useContactList, useCreateConversation } from "hooks";
import { StorageUtil } from "utils";
import { getSavedServer } from "utils/branch.utils";
import { useSelector } from "react-redux";
import { BranchSelectors } from "redux-store";
import clsx from "clsx";

const AddingPersonalDrawer = ({ onClose, ...otherProps }) => {
  const classes = useStyles();
  const searchingRef = useRef("");
  const { createWithPersonal } = useCreateConversation();
  const {
    accountList,
    queryPagination: { page: queryPage, totalPage, totalItems },
    getAccountList,
  } = useAccountList();
  const { contactList, isEnd: isEndContactList, getAllContactByName } = useContactList();

  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID);

  const { t: getLabel } = useTranslation(LangConstant.NS_HOME);
  const chatObjContent = getLabel(LangConstant.OBJ_ADDING_CHAT, { returnObjects: true });
  const isBranchServer = useSelector(BranchSelectors.isBranchServer);

  const [viewingList, setViewingList] = useState([]);

  const onCreateNewHistoryKeyword = () => {
    const savedServer = getSavedServer();
    if (searchingRef.current) {
      let historyKeywords = StorageUtil.getItem(KeyConstant.KEY_LOCALSTORAGE_SIDEBAR_KEYWORDS) || {};
      let historyKeywordsInServer = historyKeywords[savedServer?.id] || [];
      historyKeywordsInServer.unshift(searchingRef.current);
      StorageUtil.setItem(
        KeyConstant.KEY_LOCALSTORAGE_SIDEBAR_KEYWORDS,
        Object.assign(historyKeywords, { [savedServer?.id]: historyKeywordsInServer }),
        StorageUtil.getCurrentPrefixKey(),
      );
    }
  };

  const onStartPersonalChat = item => {
    onCreateNewHistoryKeyword();

    createWithPersonal(item);
    onClose();
  };

  const onSearchContact = async searchingContent => {
    searchingRef.current = searchingContent?.trim() || "";

    if (isBranchServer && searchingRef.current) {
      getAccountList({ keyword: searchingRef.current, page: 1 });
    } else {
      getAllContactByName(searchingRef.current);
    }
  };

  const handleLoadMore = () => {
    if (isBranchServer && searchingRef.current && queryPage <= totalPage) {
      getAccountList({ keyword: searchingRef.current, page: queryPage + 1 });
    } else if (!isEndContactList) {
      getAllContactByName(searchingRef.current);
    }
  };

  useEffect(() => {
    onSearchContact("");
  }, []);

  useEffect(() => {
    let filteredList = [];
    if (isBranchServer && searchingRef.current) {
      filteredList = accountList;
    } else {
      filteredList = contactList;
    }
    setViewingList(filteredList);
  }, [accountList, contactList]);

  const totalMembers = isBranchServer && searchingRef.current ? totalItems : viewingList.length;

  return (
    <AppDrawer title={chatObjContent.title} onClose={onClose} {...otherProps}>
      <Box className={classes.rootSpace}>
        <SearchBar onChange={onSearchContact} />
      </Box>

      <Box className={classes.selectedList}>
        <Typography variant="subtitle2" className={clsx(classes.subTitle, classes.rootSpace)}>
          {getLabel(LangConstant.FM_MEMBER, { number: totalMembers })}
        </Typography>

        <InfiniteScroll className={classes.infiniteScroll} onScrollToBottom={handleLoadMore}>
          {viewingList.map((contact, index) => (
            <Box key={`${contact.avatarId}-${index}`}>
              {index > 0 && <Divider variant="inset" />}
              <AvatarInfo
                avatar={{ avatarId: contact.avatarId, name: contact.contactName || contact.name || contact.phone }}
                title={contact.contactName || contact.name || contact.phone}
                onClick={() => onStartPersonalChat(contact)}
                button={true}
                sendToMe={contact.id === accountId}
              />
            </Box>
          ))}
        </InfiniteScroll>
      </Box>
    </AppDrawer>
  );
};

AddingPersonalDrawer.propTypes = AppDrawer.propTypes;

export default AddingPersonalDrawer;

const useStyles = makeStyles(theme => ({
  rootSpace: {
    paddingLeft: 16,
    paddingRight: 16,
  },

  subTitle: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    textAlign: "left",
    marginTop: 10,
  },

  selectedList: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
  },

  infiniteScroll: {
    height: "calc(100vh - 135px)",
    overflow: "auto",
    padding: "16px 14px",
  },
}));
